import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Users, 
  Search, 
  Filter, 
  Plus,
  Eye,
  MessageSquare,
  Calendar,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
  Brain,
  Video,
  MapPin,
  Mail,
  Phone,
  Loader,
  Share2,
  UserCheck,
  UserPlus,
  UserMinus,
  Edit,
  Upload
} from 'lucide-react'
import { apiClient, handleApiError } from '@/lib/api'
import CandidateFormModal from '@/components/candidates/CandidateFormModal'
import ShareCandidateModal from '@/components/candidates/ShareCandidateModal'
import CandidateEditModal from '@/components/candidates/CandidateEditModal'
import SortableTable, { Column } from '@/components/common/SortableTable'

interface Candidate {
  id: string
  name: string
  email: string
  phone: string
  currentPosition: string
  noticePeriod: string
  overallScore: number
  technicalScore: number
  behavioralScore: number
  aiScore: number
  interviewDate?: string
  appliedDate: string
  skills: string[]
  primarySkills: string[]
  allSkills: string[]
  experience: number
  education: string
  location: string
  expectedSalary?: number
  resumeUrl?: string
  interviewNotes?: string
  recruiterRating: number
  aiInsights: string[]
  // Sharing-related properties
  ownership_type?: 'owned' | 'shared'
  original_owner_name?: string
  share_type?: 'read_only' | 'collaborate' | 'transfer'
  is_shared?: boolean
  shareType?: 'created' | 'shared_individual' | 'shared_team'
  sharedBy?: string
  sharedByName?: string
  sharedTeamName?: string
  isCreatedByMe?: boolean
  isFollowing?: boolean
  // Edit functionality properties
  can_edit_candidate?: boolean
  not_registered?: boolean
  created_by_me?: boolean
}


export default function CandidatesPage() {
  const navigate = useNavigate()
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [loading, setLoading] = useState(true)
  const [showTable, setShowTable] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    loadCandidates()
  }, [])

  const loadCandidates = async () => {
    try {
      setLoading(true)
      const response = await apiClient.getCandidates({
        ...(searchQuery ? { search: searchQuery } : {})
      })

      if (response.success && response.data) {
        const candidateData = Array.isArray(response.data) ? response.data : response.data.candidates
        if (Array.isArray(candidateData)) {
          setCandidates(candidateData as Candidate[])
        } else {
          setCandidates([])
        }
      } else {
        setCandidates([])
      }
    } catch (err) {
      setCandidates([])
      console.error('Failed to load candidates:', err)
    } finally {
      setLoading(false)
    }
  }


  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-200'
    if (score >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200'
    if (score > 0) return 'text-red-600 bg-red-50 border-red-200'
    return 'text-gray-600 bg-gray-50 border-gray-200'
  }

  const handleViewCandidate = (candidateId: string) => {
    navigate(`/recruiter/candidates/${candidateId}`)
  }

  const handleScheduleInterview = async (candidateId: string) => {
    try {
      const candidate = Array.isArray(candidates) ? candidates.find(c => c.id === candidateId) : null
      if (!candidate) return
      
      const response = await apiClient.quickScheduleInterview(candidateId, candidate.positionId)
      if (response.success) {
        alert('Interview scheduled successfully!')
        loadCandidates()
      }
    } catch (err) {
      alert('Failed to schedule interview: ' + handleApiError(err))
    }
  }

  const handleMessageCandidate = (candidateId: string) => {
    navigate(`/recruiter/messages?candidate=${candidateId}`)
  }


  const handleCreateCandidate = () => {
    setShowCreateModal(true)
  }

  const handleCloseCreateModal = () => {
    setShowCreateModal(false)
  }

  const handleShareCandidate = (candidate: Candidate) => {
    setSelectedCandidate(candidate)
    setShowShareModal(true)
  }

  const handleCloseShareModal = () => {
    setShowShareModal(false)
    setSelectedCandidate(null)
    loadCandidates() // Refresh to show updated sharing status
  }

  const handleEditCandidate = (candidate: Candidate) => {
    setSelectedCandidate(candidate)
    setShowEditModal(true)
  }

  const handleCloseEditModal = () => {
    setShowEditModal(false)
    setSelectedCandidate(null)
    loadCandidates() // Refresh to show updated candidate data
  }

  const handleToggleActive = async (candidateId: string) => {
    try {
      const candidate = candidates.find(c => c.id === candidateId)
      if (!candidate) return
      
      const newStatus = candidate.isActive ? 'inactive' : 'active'
      const response = await apiClient.updateCandidate(candidateId, { status: newStatus })
      
      if (response.success) {
        loadCandidates() // Refresh the list
      } else {
        alert('Failed to update candidate status')
      }
    } catch (err) {
      alert('Failed to update candidate status: ' + handleApiError(err))
    }
  }

  const handleToggleFollow = async (candidateId: string, isCurrentlyFollowing: boolean) => {
    try {
      let response
      if (isCurrentlyFollowing) {
        response = await apiClient.unfollowCandidate(candidateId)
      } else {
        response = await apiClient.followCandidate(candidateId)
      }

      if (response.success) {
        if (isCurrentlyFollowing) {
          // Remove unfollowed candidate from the list since this page only shows followed candidates
          setCandidates(prev => prev.filter(candidate => candidate.id !== candidateId))
        } else {
          // Update the local state for follow action (though this shouldn't happen on this page)
          setCandidates(prev => prev.map(candidate => 
            candidate.id === candidateId 
              ? { ...candidate, isFollowing: true }
              : candidate
          ))
        }
      } else {
        alert('Failed to update follow status')
      }
    } catch (err) {
      alert('Failed to update follow status: ' + handleApiError(err))
    }
  }

  const candidateColumns: Column<Candidate>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      filterable: true,
      render: (_, candidate) => (
        <div className="flex items-center space-x-3">
          <div 
            className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-400 transition-colors"
            onClick={() => window.open(`/public/candidate-profile/${candidate.id}`, '_blank')}
          >
            <span className="text-sm font-medium text-gray-700">
              {candidate.name ? candidate.name.split(' ').map((n: string) => n[0]).join('') : '?'}
            </span>
          </div>
          <div>
            <div 
              className="font-medium text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
              onClick={() => window.open(`/public/candidate-profile/${candidate.id}`, '_blank')}
            >
              {candidate.name || 'Unknown'}
            </div>
            <div className="text-sm text-gray-500">{candidate.email}</div>
          </div>
        </div>
      )
    },
    {
      key: 'currentPosition',
      label: 'Current Position',
      sortable: true,
      filterable: true,
      render: (position) => (
        <div className="text-sm">
          <span className="font-medium text-gray-900">{position || 'Not specified'}</span>
        </div>
      )
    },
    {
      key: 'location',
      label: 'Location',
      sortable: true,
      filterable: true,
      render: (location) => (
        <div className="flex items-center">
          <MapPin className="w-4 h-4 text-gray-400 mr-1" />
          <span className="text-sm">{location || 'Not specified'}</span>
        </div>
      )
    },
    {
      key: 'noticePeriod',
      label: 'Notice Period',
      sortable: true,
      filterable: true,
      render: (noticePeriod) => (
        <div className="text-sm">
          <span className="text-gray-900">{noticePeriod || 'Not specified'}</span>
        </div>
      )
    },
    {
      key: 'primarySkills',
      label: 'Primary Skills',
      sortable: false,
      filterable: true,
      render: (_, candidate) => (
        <div className="flex flex-wrap gap-1">
          {candidate.primarySkills && Array.isArray(candidate.primarySkills) ? (
            candidate.primarySkills.slice(0, 3).map((skill, index) => (
              <span key={index} className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                {skill}
              </span>
            ))
          ) : (
            <span className="text-gray-400 text-sm">No skills listed</span>
          )}
          {candidate.allSkills && candidate.allSkills.length > 3 && (
            <span className="text-xs text-gray-500">+{candidate.allSkills.length - 3} more</span>
          )}
        </div>
      )
    },
    {
      key: 'allSkills',
      label: 'All Skills',
      sortable: false,
      filterable: true,
      render: (_, candidate) => (
        <div className="max-w-xs">
          <div className="flex flex-wrap gap-1">
            {candidate.allSkills && Array.isArray(candidate.allSkills) ? (
              candidate.allSkills.slice(0, 4).map((skill, index) => (
                <span key={index} className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                  {skill}
                </span>
              ))
            ) : (
              <span className="text-gray-400 text-sm">No skills listed</span>
            )}
            {candidate.allSkills && candidate.allSkills.length > 4 && (
              <span className="text-xs text-gray-500">+{candidate.allSkills.length - 4} more</span>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      filterable: false,
      render: (_, candidate) => (
        <div className="flex items-center space-x-2">
          {/* Show edit button if candidate was created by recruiter and hasn't registered */}
          {candidate.can_edit_candidate && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleEditCandidate(candidate)
              }}
              className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full transition-colors bg-blue-100 text-blue-800 hover:bg-blue-200"
              title="Edit candidate profile"
            >
              <Edit className="w-3 h-3 mr-1" />
              Edit
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleToggleFollow(candidate.id, true) // Always true since all candidates on this page are followed
            }}
            className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full transition-colors bg-red-100 text-red-800 hover:bg-red-200"
            title="Unfollow candidate"
          >
            <UserMinus className="w-3 h-3 mr-1" />
            Unfollow
          </button>
        </div>
      )
    }
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading candidates...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">All Candidates</h1>
          <p className="text-gray-600 mt-1">Manage and track all candidates across positions</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button onClick={handleCreateCandidate} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Add Candidate
          </button>
        </div>
      </div>

      {/* Candidates Table */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {Array.isArray(candidates) ? candidates.length : 0} Candidates
          </h2>
        </div>
        
        {Array.isArray(candidates) && candidates.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No candidates found</h3>
            <p className="text-gray-600 mb-6">Start building your talent pipeline by adding candidates.</p>
            <button onClick={handleCreateCandidate} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add Candidate
            </button>
          </div>
        ) : (
          <SortableTable
            data={candidates}
            columns={candidateColumns}
            onRowClick={(candidate) => handleViewCandidate(candidate.id)}
          />
        )}
      </div>

      {/* Create Candidate Modal */}
      <CandidateFormModal 
        open={showCreateModal} 
        onClose={handleCloseCreateModal} 
        onSuccess={loadCandidates}
      />

      {/* Share Candidate Modal */}
      <ShareCandidateModal 
        open={showShareModal} 
        onClose={handleCloseShareModal}
        candidate={selectedCandidate}
      />

      {/* Edit Candidate Modal */}
      <CandidateEditModal 
        open={showEditModal} 
        onClose={handleCloseEditModal}
        candidate={selectedCandidate}
        onSuccess={loadCandidates}
      />
    </div>
  )
}
